import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import React, { useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CreditCard, Check, ErrorOutline } from '@mui/icons-material';

import Loading from '../../components/global/loading/Loading';
import { useTransaction } from '../../context/TransactionContext';
import CheckoutForm from './CheckoutForm';

type PaymentFormProps = {
  onComplete: () => void;
};

function PaymentForm({ onComplete }: PaymentFormProps) {
  const { id } = useParams();
  const navigate = useNavigate();

  const { getStripePublishableKey, createPaymentIntent } = useTransaction();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [stripePromise, setStripePromise] =
    React.useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = React.useState<
    'loading' | 'ready' | 'processing' | 'success' | 'error'
  >('loading');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    if (!id) {
      navigate(-1);
      toast.error('Brak numeru zamówienia');
      return;
    }

    try {
      const clientSecret = await createPaymentIntent(id);
      setClientSecret(clientSecret);
      setPaymentStatus('ready');
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ||
          'Wystąpił błąd podczas tworzenia płatności',
      );
      setPaymentStatus('error');
      toast.error(
        error?.response?.data?.message ||
          'Wystąpił błąd podczas tworzenia płatności',
      );
    }
  }, [id, navigate, createPaymentIntent]);

  useEffect(() => {
    const fetchStripePublishableKey = async () => {
      try {
        const stripePublishableKey = await getStripePublishableKey();
        const stripe = loadStripe(stripePublishableKey);
        setStripePromise(stripe);
      } catch (error: any) {
        setErrorMessage('Nie udało się załadować klucza publicznego Stripe');
        setPaymentStatus('error');
      }
    };

    setPaymentStatus('loading');
    fetchStripePublishableKey();
    fetchClientSecret();
  }, [fetchClientSecret, getStripePublishableKey]);

  const handlePaymentSuccess = () => {
    setPaymentStatus('success');
    // Powiadom komponent nadrzędny Cart o ukończeniu płatności
    onComplete();
  };

  const handlePaymentProcessing = () => {
    setPaymentStatus('processing');
    setIsSubmitting(true);
  };

  const handlePaymentError = (message: string) => {
    setPaymentStatus('error');
    setErrorMessage(message);
    setIsSubmitting(false);
  };

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  if (paymentStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-full min-h-[400px]">
        <Loading />
        <p className="text-gray-500 mt-4">Przygotowujemy płatność...</p>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <motion.div
        className="p-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          variants={itemVariants}
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <ErrorOutline className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Wystąpił błąd płatności
              </h3>
              <p className="mt-2 text-sm text-red-700">
                {errorMessage || 'Nie można przetworzyć płatności'}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-4 justify-center mt-6"
        >
          <motion.button
            className="px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-md text-sm font-medium transition-colors"
            onClick={() => navigate(`/zamówienie/${id}`)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Wróć do zamówienia
          </motion.button>
          <motion.button
            className="px-4 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
            onClick={() => {
              setPaymentStatus('loading');
              fetchClientSecret();
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Spróbuj ponownie
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <motion.div
        className="p-6 flex flex-col items-center justify-center min-h-[400px]"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="bg-green-50 rounded-full p-4 mb-4"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <Check className="h-12 w-12 text-green-500" />
        </motion.div>
        <motion.h2
          className="text-xl font-medium text-gray-900 mb-2"
          variants={itemVariants}
        >
          Płatność zrealizowana pomyślnie
        </motion.h2>
        <motion.p
          className="text-gray-600 text-center mb-6"
          variants={itemVariants}
        >
          Twoje zamówienie zostało opłacone. Wkrótce otrzymasz email z
          potwierdzeniem.
        </motion.p>
        <motion.button
          className="px-6 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
          onClick={() => navigate('/')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          variants={itemVariants}
        >
          Wróć do sklepu
        </motion.button>
      </motion.div>
    );
  }

  if (!id) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-700">Brak zamówienia do opłacenia</p>
        <button
          className="mt-4 px-4 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium"
          onClick={() => navigate('/')}
        >
          Wróć do sklepu
        </button>
      </div>
    );
  }

  return (
    <motion.div
      className="p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="flex items-center mb-6">
        <CreditCard className="text-gray-500 mr-2" />
        <h2 className="text-lg font-medium text-gray-800">Dane płatności</h2>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-6"
      >
        <p className="text-sm text-gray-600 mb-2">
          Wszystkie dane przesyłane są bezpiecznie za pomocą szyfrowania SSL.
          Twoje dane płatnicze nigdy nie są przechowywane na naszych serwerach.
        </p>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="bg-white rounded-lg border border-gray-200 p-6"
      >
        {stripePromise && clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: 'stripe' } }}
          >
            <CheckoutForm
              orderId={id}
              onSuccess={handlePaymentSuccess}
              onProcessing={handlePaymentProcessing}
              onError={handlePaymentError}
              isSubmitting={isSubmitting}
            />
          </Elements>
        ) : (
          <div className="flex justify-center p-4">
            <Loading />
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default PaymentForm;
